/**
 * Opens the study 1:1 for a twin.
 *
 * Reuses a verified open room for the same study + audience + persona when
 * one exists. The recall, GET, create, and remember steps for that key run
 * under one lock so two tabs cannot both create a room. A room published
 * while GET is in flight is kept: deletion is conditional on the token that
 * was verified, and create stores a token only when the slot is still empty.
 * The room carries only the study id: the twin's saved answers are
 * server-owned and loaded by Nest after authorization, never sent by the
 * client. The chat URL is armed for crash recovery before the client
 * navigates, so a renderer kill cannot only know about Results.
 */

import type { ChatRoomDto, CreateChatRoomDto, StudyCreative } from '@digital-twins/shared-types'
import { armChatCrashRestore } from './chatCrashRestore'
import {
  isReusableOneOnOneRoom,
  publishCreatedOneOnOneRoom,
  recallOneOnOneRoom,
  settleVerifiedOneOnOneRoom,
  withOneOnOneRoomLock,
  OneOnOneRoomKey,
} from './oneOnOneRoomMemory'

export interface OpenStudyTwinChatInput {
  populationId: string
  personaId: string
  experimentId?: string
  experimentLanguage?: string | null
  imageUrl?: string
  bio?: string
  creatives: Array<StudyCreative>
  backHref: string
}

export interface OpenStudyTwinChatDeps {
  getRoom: (token: string) => Promise<ChatRoomDto>
  createRoom: (dto: CreateChatRoomDto) => Promise<ChatRoomDto>
  navigate: (href: string, backHref: string) => void
}

function chatHref(token: string): string {
  return `/chats/${encodeURIComponent(token)}`
}

function buildCreateDto(input: OpenStudyTwinChatInput): CreateChatRoomDto {
  return {
    populationId: input.populationId,
    participantPersonaIds: [input.personaId],
    settings: {
      language: input.experimentLanguage || 'en',
      visibility: 'private',
      // Creative images (imagesObjectKey) are injected server-side from
      // studyContext.experimentId — never client-supplied.
      ...(input.creatives.length > 0
        ? { stimulusImagePolicy: 'always' as const }
        : {}),
    },
    ...(input.imageUrl
      ? { participantImages: { [input.personaId]: input.imageUrl } }
      : {}),
    ...(input.bio
      ? { participantBios: { [input.personaId]: input.bio } }
      : {}),
    ...(input.experimentId
      ? {
          studyContext: {
            experimentId: input.experimentId,
          },
        }
      : {}),
  }
}

function goToRoom(
  token: string,
  input: OpenStudyTwinChatInput,
  navigate: OpenStudyTwinChatDeps['navigate'],
): void {
  const href = chatHref(token)
  armChatCrashRestore({
    href,
    roomToken: token,
    studyId: input.experimentId ?? null,
    backHref: input.backHref,
  })
  navigate(href, input.backHref)
}

async function resolveOneOnOneRoom(
  key: OneOnOneRoomKey,
  input: OpenStudyTwinChatInput,
  deps: OpenStudyTwinChatDeps,
): Promise<'reused' | 'created'> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const remembered = await recallOneOnOneRoom(key)
    if (!remembered) {
      const room = await deps.createRoom(buildCreateDto(input))
      const token = await publishCreatedOneOnOneRoom(key, room.token)
      goToRoom(token, input, deps.navigate)
      return token === room.token ? 'created' : 'reused'
    }

    let reusable = false
    try {
      const existing = await deps.getRoom(remembered.token)
      reusable = isReusableOneOnOneRoom(existing, key)
    } catch {
      reusable = false
    }

    const settlement = await settleVerifiedOneOnOneRoom(key, remembered.token, reusable)
    if (settlement === 'reused') {
      goToRoom(remembered.token, input, deps.navigate)
      return 'reused'
    }
    if (settlement === 'replaced') {
      continue
    }
    if (await recallOneOnOneRoom(key)) {
      continue
    }
  }

  const leftover = await recallOneOnOneRoom(key)
  if (leftover) {
    goToRoom(leftover.token, input, deps.navigate)
    return 'reused'
  }
  const room = await deps.createRoom(buildCreateDto(input))
  const token = await publishCreatedOneOnOneRoom(key, room.token)
  goToRoom(token, input, deps.navigate)
  return token === room.token ? 'created' : 'reused'
}

export async function openStudyTwinChat(
  input: OpenStudyTwinChatInput,
  deps: OpenStudyTwinChatDeps,
): Promise<'reused' | 'created'> {
  if (!input.experimentId) {
    const room = await deps.createRoom(buildCreateDto(input))
    goToRoom(room.token, input, deps.navigate)
    return 'created'
  }

  const key = {
    populationId: input.populationId,
    personaId: input.personaId,
    experimentId: input.experimentId,
  }
  // Verify and create hold this twin's lock. Storage itself is a separate
  // index lock, so another tab can publish a replacement during GET and
  // this flow will keep that token instead of deleting it.
  return withOneOnOneRoomLock(key, () => resolveOneOnOneRoom(key, input, deps))
}
